import {
  FormSubmitButton,
  FormSwitchField,
  FormProvider,
  useForm,
} from '@mfw/forms';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIntl, Button } from '@mfw/components';
import Box from '@mui/material/Box';

import type { UiUserHealthIssuesStep } from '../../../../../schemas/users.schema';
import type { ClientOnboardingStepsType } from '../../clientOnboarding.steps';

import useCreateOnboardingUser from '../../../../../hooks/users/useCreateOnboardingUser';
import { UiUserHealthIssuesStepSchema } from '../../../../../schemas/users.schema';
import usePublicHealthIssues from '../../../../../hooks/usePublicHealthIssues';
import { getLabelFromTranslations } from '../../../../../utils';
import { useUpdateUser } from '../../../../../hooks/usersv2';
import OnboardingHeader from '../../../OnboardingHeader';
import { useAuthContext } from '../../../../../context';
import { useWizard } from '../../../../../allFeatures';
import generalMessages from '../../../../../messages';
import messages from '../HealthIssuesStep/messages';

export const HealthIssuesDialogStep = () => {
  const { currentUser } = useAuthContext();
  const { formatMessage, locale } = useIntl();
  const { goToStep } = useWizard<ClientOnboardingStepsType>();
  const { mutateAsync: updateUserV2Async } = useUpdateUser();
  const { mutateAsync: createOnboardingUserAsync } = useCreateOnboardingUser();

  const methods = useForm({
    defaultValues: {
      healthIssues: currentUser?.fitnessProfile?.healthIssues ?? [],
    },
    resolver: zodResolver(UiUserHealthIssuesStepSchema),
  });

  const handleNext = async (data: UiUserHealthIssuesStep) => {
    await updateUserV2Async({
      fitnessProfile: {
        healthIssues: data.healthIssues,
      },
    });
    await createOnboardingUserAsync({
      address: currentUser?.address ?? undefined,
      contact: currentUser?.contact ?? undefined,
      email: currentUser?.email ?? '',
      name: currentUser?.name ?? '',
      type: 'client',
    });
    goToStep('pendingQuestionnaires');
  };

  const { data, fetchNextPage } = usePublicHealthIssues({
    filters: { limit: '50', orderBy: 'name', status: 'active' },
  });

  return (
    <FormProvider onSubmit={handleNext} {...methods}>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          position: 'sticky',
          px: 3,
          py: 2,
          top: 0,
          zIndex: 2,
        }}
      >
        <OnboardingHeader
          description={<p>{formatMessage(messages.description)}</p>}
          title={formatMessage(messages.title)}
        />
      </Box>
      <Box px={3}>
        {data?.data?.map((healthIssue, index) => (
          <FormSwitchField
            defaultChecked={currentUser?.fitnessProfile?.healthIssues?.some(
              (issue) => issue.value === healthIssue.id && issue.checked,
            )}
            label={getLabelFromTranslations(locale, healthIssue.translations)}
            data-testid="client-onboarding-health-issue"
            isObserved={index === data.data.length - 1}
            name={`healthIssues.${index}`}
            onIntersection={fetchNextPage}
            value={healthIssue.id}
            key={healthIssue.id}
          />
        ))}
      </Box>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          bottom: 0,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          position: 'sticky',
          px: 2,
          py: 1.5,
          zIndex: 1,
        }}
      >
        <Button
          onClick={() => goToStep('fitnessGoals')}
          data-testid="onboarding-back"
          variant="outlined"
          size="large"
        >
          {formatMessage(generalMessages.back)}
        </Button>
        <FormSubmitButton
          data-testid="onboarding-next"
          variant="contained"
          disableIfNotDirty
          size="large"
        >
          {formatMessage(generalMessages.next)}
        </FormSubmitButton>
      </Box>
    </FormProvider>
  );
};
